import { useEffect, useState, useCallback } from "react";
import { ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, brl, errMsg, fmtDateTime, fmtQty } from "@/lib/api";
import { toast } from "sonner";

export function Inventory({ canChange }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const load = useCallback(() => Promise.all([api.get("/stock/items"), api.get("/stock/inventories")]).then(([i, h]) => { setItems(i.data); setHistory(h.data); setCounts({}); }), []);
  useEffect(() => { load(); }, [load]);
  const filled = items.filter((i) => counts[i.id] !== undefined && counts[i.id] !== "");
  const submit = async () => {
    try {
      const { data } = await api.post("/stock/inventory", { note, counts: filled.map((i) => ({ kind: i.kind, item_id: i.id, counted: Number(counts[i.id]) })) });
      toast.success(`Inventário concluído · ${data.divergences} divergência(s)`);
      setNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      <div className="card-surface overflow-x-auto">
        <Table data-testid="inventory-table">
          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Saldo no sistema</TableHead><TableHead className="w-40">Contagem</TableHead><TableHead className="text-right">Diferença</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((i) => {
              const c = counts[i.id];
              const diff = c === undefined || c === "" ? null : Number(c) - i.stock;
              return (
                <TableRow key={i.id}>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="text-right font-mono">{fmtQty(i.stock)} {i.unit}</TableCell>
                  <TableCell><Input type="number" step="0.001" disabled={!canChange} value={c ?? ""} onChange={(e) => setCounts((x) => ({ ...x, [i.id]: e.target.value }))} className="h-9" data-testid={`inventory-count-${i.id}`} /></TableCell>
                  <TableCell className={`text-right font-mono ${diff < 0 ? "text-red-500" : diff > 0 ? "text-emerald-500" : ""}`}>{diff === null ? "—" : `${diff > 0 ? "+" : ""}${fmtQty(diff)}`}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {canChange && (
        <div className="flex flex-wrap gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observação do inventário" className="max-w-sm" data-testid="inventory-note-input" />
          <Button disabled={!filled.length} onClick={submit} data-testid="inventory-submit-btn"><ClipboardCheck className="h-4 w-4 mr-1" />Concluir inventário ({filled.length} itens)</Button>
        </div>
      )}
      <div className="card-surface">
        <p className="eyebrow border-b p-4">Inventários anteriores</p>
        <ul className="divide-y text-sm" data-testid="inventory-history">
          {history.map((h) => <li key={h.id} className="flex flex-wrap justify-between gap-2 p-4"><span>{fmtDateTime(h.created_at)} · {h.user} {h.note ? `· ${h.note}` : ""}</span><span className="text-muted-foreground">{h.items_count} itens · {h.divergences} divergência(s) · <b className={h.value_diff < 0 ? "text-red-500" : ""}>{brl(h.value_diff)}</b></span></li>)}
          {!history.length && <li className="p-4 text-muted-foreground">Nenhum inventário realizado.</li>}
        </ul>
      </div>
    </div>
  );
}
